import { useCallback, useEffect, useRef, useState } from 'react'
import API from '../../../../services/API'

export function useControlRoomLogs(
  controlRoomID,
  pollMs = 2000,
  pageSize = 20,
  // onNewLogs: called with logs that appeared through polling (never the
  // initial page load or older pages) — used for the log chime
  { onNewLogs } = {},
) {
  const [logs, setLogs] = useState([]) // newest → oldest
  const [loading, setLoading] = useState(true)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [error, setError] = useState(null)

  const newestRef = useRef(null)
  const oldestRef = useRef(null)
  const pollingRef = useRef(null)
  // Every log id this hook has seen, so polled batches can be split into
  // genuinely-new vs updated without side effects inside state updaters
  const knownIdsRef = useRef(new Set())
  const onNewLogsRef = useRef(onNewLogs)
  onNewLogsRef.current = onNewLogs

  const reset = useCallback(() => {
    setLogs([])
    setError(null)
    newestRef.current = null
    oldestRef.current = null
    knownIdsRef.current = new Set()
  }, [])

  const setBoundsFromBatch = (batch) => {
    if (!batch?.length) return

    newestRef.current = {
      createdAt: batch[0].createdAt,
      _id: batch[0]._id,
    }

    oldestRef.current = {
      createdAt: batch[batch.length - 1].createdAt,
      _id: batch[batch.length - 1]._id,
    }
  }

  const loadInitial = useCallback(async () => {
    if (!controlRoomID) return

    setLoading(true)
    setError(null)

    try {
      const { data } = await API.get(`/controlroom/${controlRoomID}/logs`, {
        params: { limit: pageSize },
      })

      const batch = data.logs || []

      setLogs(batch)
      setBoundsFromBatch(batch)
      knownIdsRef.current = new Set(batch.map((x) => x._id))
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [controlRoomID, pageSize])

  const loadNewer = useCallback(async () => {
    if (!controlRoomID || !newestRef.current) return

    const { createdAt, _id } = newestRef.current

    try {
      const { data } = await API.get(`/controlroom/${controlRoomID}/logs`, {
        params: {
          afterCreatedAt: createdAt,
          afterId: _id,
        },
      })

      if (!data.logs?.length) return

      const newestFirst = data.logs
      const freshLogs = newestFirst.filter(
        (x) => !knownIdsRef.current.has(x._id),
      )
      freshLogs.forEach((x) => knownIdsRef.current.add(x._id))

      setLogs((prev) => {
        const incomingById = new Map(newestFirst.map((x) => [x._id, x]))

        const updatedExisting = prev.map((existing) =>
          incomingById.has(existing._id)
            ? { ...existing, ...incomingById.get(existing._id) }
            : existing,
        )

        const existingIds = new Set(prev.map((x) => x._id))
        const genuinelyNew = newestFirst.filter((x) => !existingIds.has(x._id))

        return [...genuinelyNew, ...updatedExisting]
      })

      newestRef.current = {
        createdAt: newestFirst[0].createdAt,
        _id: newestFirst[0]._id,
      }

      if (freshLogs.length) onNewLogsRef.current?.(freshLogs)
    } catch {
      // Ignore transient polling errors
    }
  }, [controlRoomID])

  const loadOlder = useCallback(async () => {
    if (!controlRoomID || !oldestRef.current || loadingOlder) return

    setLoadingOlder(true)

    const { createdAt, _id } = oldestRef.current

    try {
      const { data } = await API.get(`/controlroom/${controlRoomID}/logs`, {
        params: {
          beforeCreatedAt: createdAt,
          beforeId: _id,
          limit: pageSize,
        },
      })

      if (!data.logs?.length) return

      data.logs.forEach((x) => knownIdsRef.current.add(x._id))
      setLogs((prev) => {
        const existingIds = new Set(prev.map((x) => x._id))
        const deduped = data.logs.filter((x) => !existingIds.has(x._id))

        return [...prev, ...deduped]
      })

      const existingIds = new Set(logs.map((x) => x._id))
      const deduped = data.logs.filter((x) => !existingIds.has(x._id))

      if (!deduped.length) return

      const last = deduped[deduped.length - 1]

      oldestRef.current = {
        createdAt: last.createdAt,
        _id: last._id,
      }
    } catch (err) {
      setError(err)
    } finally {
      setLoadingOlder(false)
    }
  }, [controlRoomID, pageSize, loadingOlder, logs])

  useEffect(() => {
    if (!controlRoomID) return

    reset()
    loadInitial()
  }, [controlRoomID, reset, loadInitial])

  useEffect(() => {
    if (!controlRoomID || !pollMs) return

    pollingRef.current = setInterval(loadNewer, pollMs)

    return () => {
      clearInterval(pollingRef.current)
    }
  }, [controlRoomID, pollMs, loadNewer])

  return {
    logs,
    loading,
    error,
    loadOlder,
    loadingOlder,
    refresh: loadInitial,
  }
}
